import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { instrumentRoutePerformance } from '@sentry/ember';
import { initializeVdrClient } from 'scaleflow/utils/aws-clients/s3VDRClient';
import type { S3ObjectSummary } from 'scaleflow/utils/aws-clients/s3VDRClient';
import { libraryPrefix } from 'scaleflow/utils/manual-repo-archives';
import type StoreService from 'scaleflow/services/store';
import type Transition from '@ember/routing/transition';
import type ConnectorsManualController from 'scaleflow/controllers/connectors/manual';

export type ManualUploadModel = {
  archives: S3ObjectSummary[];
  /** True when the listing itself failed, as opposed to there being nothing. */
  loadFailed: boolean;
};

class ConnectorsManualRoute extends Route {
  @service declare store: StoreService;
  @service declare organisation: any;

  /**
   * A failed listing is caught rather than thrown. The guide and the uploader
   * are independently useful, and letting a transient S3 error replace the
   * whole page with an error route would take them away too — so the page
   * renders with an inline notice instead.
   */
  async model(): Promise<ManualUploadModel> {
    try {
      const client = await initializeVdrClient(this.store);
      const archives = await client.listObjects(
        libraryPrefix(this.organisation.id as string),
      );
      return { archives, loadFailed: false };
    } catch (error) {
      console.error('Failed to list manually uploaded source code', error);
      return { archives: [], loadFailed: true };
    }
  }

  setupController(
    controller: ConnectorsManualController,
    model: ManualUploadModel,
    transition: Transition,
  ) {
    super.setupController(controller, model, transition);
    // The controller owns a tracked copy so uploads and deletes can update the
    // list without a full route refresh.
    controller.archives = model.archives;
    controller.loadFailed = model.loadFailed;
  }
}

export default instrumentRoutePerformance(ConnectorsManualRoute);
